import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import type { TriState, TriStateCheckboxProps } from "./TriStateCheckbox.types";

const TriStateCheckbox: React.FC<TriStateCheckboxProps> = ({
  state,
  onChange,
  shape = "square",
  children,
  className = "",
  enableTriState,
  size = "regular",
  checkBoxClassName = "",
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = state === "indeterminate";
    }
  }, [state]);

  const handleClick = () => {
    let nextState: TriState = state;
    if (enableTriState) {
      nextState =
        state === "unchecked"
          ? "checked"
          : state === "checked"
          ? "indeterminate"
          : "unchecked";
    } else {
      nextState =
        state === "checked" || state === "indeterminate"
          ? "unchecked"
          : "checked";
    }
    onChange(nextState);
  };

  const shapeClasses = shape === "rounded" ? "rounded-full" : "rounded";
  const isActive = state === "checked" || state === "indeterminate";

  return (
    <div
      className={classNames(
        "inline-flex items-center space-x-2 cursor-pointer select-none",
        className
      )}
    >
      <div
        onClick={handleClick}
        className={classNames(
          "border-2 flex items-center justify-center relative transition-all duration-200 ease-in-out",
          shapeClasses,
          checkBoxClassName,
          isActive ? "bg-primary-500 border-primary-500" : "bg-white border-secondary-10",
          size === "small" ? "w-4 h-4" : "w-6 h-6"
        )}
      >
        <input
          type="checkbox"
          ref={checkboxRef}
          checked={state === "checked"}
          onChange={() => {}}
          className="hidden"
        />
        <svg
          className={classNames(
            "w-4 h-4 text-white transition-all duration-200 transform",
            state === "checked"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-75 pointer-events-none"
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>

        <svg
          className={classNames(
            "w-4 h-4 text-white absolute transition-all duration-200 transform",
            state === "indeterminate"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-75 pointer-events-none"
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h12" />
        </svg>
      </div>

      {children && <span className="text-text-500">{children}</span>}
    </div>
  );
};

export default TriStateCheckbox;
